import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NJToastContainer } from "@engie-group/fluid-design-system-react";

import { ChatPage } from "@genai/chat";
import { CollectionPage, collectionPageLoader } from "@genai/collection";
import { AssistantPage, assistantPageLoader } from "@genai/assistant";
import { OrganizationPage, organizationPageLoader } from "@genai/organization";
import { LandingPage, landingPageLoader } from "@genai/landing";
import { browserStorage } from "@genai/browser";
import { setTheme, defaultTheme, Theme, themeStorageKey } from "@genai/themes";
import { LoaderFallback, RouteErrorFallback } from "@genai/fallback";
import { Suspense } from "react";
import { Login, loginLoader } from "@genai/auth";
import { AdminGuard } from "@genai/permissions";

const router = createBrowserRouter([
  {
    path: "/",
    loader: loginLoader,
    element: <Outlet />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: "",
        loader: loginLoader,
        element: <Login />,
      },
      {
        path: "sso/oidc/login_redirect",
        loader: landingPageLoader,
        element: <LandingPage />,
      },
      {
        path: "assistant/:assistantId/chat",
        element: <ChatPage />,
      },
      {
        path: "assistant/:assistantId/chat/:chatId",
        element: <ChatPage />,
      },
      {
        path: "admin",
        element: <AdminGuard />,
        children: [
          {
            path: "assistant",
            loader: assistantPageLoader,
            element: <AssistantPage />,
          },
          {
            path: "assistant/:assistantId",
            loader: assistantPageLoader,
            element: <AssistantPage />,
          },
          {
            path: "collection",
            loader: collectionPageLoader,
            element: <CollectionPage />,
          },
          {
            path: "collection/:collectionId",
            loader: collectionPageLoader,
            element: <CollectionPage />,
          },
          {
            path: "organization",
            loader: organizationPageLoader,
            element: <OrganizationPage />,
          },
          {
            path: "organization/:organizationId",
            loader: organizationPageLoader,
            element: <OrganizationPage />,
          },
        ],
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  setTheme(
    (browserStorage().getItem(themeStorageKey) as Theme) || defaultTheme,
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<LoaderFallback />}>
        <RouterProvider router={router} fallbackElement={<LoaderFallback />} />
      </Suspense>
      <NJToastContainer />
    </QueryClientProvider>
  );
}

export default App;
